import React, { useState, useRef } from 'react';
import { useParams } from 'react-router';
import { useSelector } from 'react-redux';

import Container from 'common/components/Container';
import SaveButton from 'common/components/SaveButton';
import SetPageTitle from 'components/Portals/SetPageTitle';
import SetPageNavAction from 'components/Portals/SetPageNavAction';
import EmptyState from 'common/components/EmptyState';
import DeliveryForm, { FormHandler } from './components/DeliveryForm';
import DeleteDeliveryDialog from './components/DeleteDeliveryDialog';

import { DeliveryOptionWithProducts } from 'common/types';
import { Routes, replace } from 'routing';
import { useTranslation } from 'services/localization/useTranslation';

import * as ShopSelectors from 'selectors/ShopSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import Spinner from 'common/components/Spinner';

const EditDelivery = () => {
	const { t } = useTranslation();
	const { deliveryId } = useParams<{ deliveryId: string }>();

	const shopDeliveryOptions = useSelector(ShopSelectors.shopDeliveryOptions);
	const isStockProductsLoading = useSelector(StockSelectors.stockProductsLoading);
	const activeLocationProducts = useSelector(StockSelectors.activeLocationProducts);

	const [isSaving, setIsSaving] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [hasChanges, setHasChanges] = useState(false);

	const formRef = useRef<FormHandler>();

	const activeDeliveryOption = shopDeliveryOptions.find((option) => option.id === deliveryId);
	const activeDeliveryOptionWithProduct: DeliveryOptionWithProducts | null = activeDeliveryOption
		? {
				...activeDeliveryOption,
				products: activeLocationProducts.filter((p) =>
					(p?.deliveryOptionIds ?? []).includes(activeDeliveryOption.id),
				),
		  }
		: null;

	return (
		<>
			<SetPageNavAction
				backAction={{
					to: Routes.DELIVERY,
					title: t('delivery.delivery', 'Delivery'),
				}}
			/>
			{isStockProductsLoading ? (
				<Spinner relative={true} margin={60} />
			) : (
				<EmptyState
					title={t('delivery.deliveryNotFound', 'Delivery not found')}
					isLoading={isDeleting}
					isEmpty={!activeDeliveryOptionWithProduct}
					callToActionText={t('delivery.backToDeliveryPage', 'Back to delivery page')}
					callToActionHandler={() => replace(Routes.DELIVERY)}
					render={() => (
						<>
							<SetPageTitle
								title={activeDeliveryOptionWithProduct?.name.def ?? ''}
								priority={2}
								rightContent={
									<SaveButton
										disabled={!hasChanges}
										loading={isSaving}
										onClick={() =>
											formRef.current?.errors
												? formRef.current?.handleInvalidForm()
												: formRef.current?.handleSubmit()
										}
										t={t}
									/>
								}
								contextActions={[
									{
										label: t('delivery.deleteDelivery', 'Delete delivery'),
										onClick: () => setDeleteDialogOpen(true),
									},
								]}
							/>
							{activeDeliveryOptionWithProduct && (
								<>
									<Container densePaddingTop>
										<DeliveryForm
											ref={formRef}
											value={activeDeliveryOptionWithProduct}
											setLoading={setIsSaving}
											setHasChanges={setHasChanges}
										/>
									</Container>
									<DeleteDeliveryDialog
										value={activeDeliveryOptionWithProduct}
										open={deleteDialogOpen}
										onClose={() => setDeleteDialogOpen(false)}
										setLoading={setIsDeleting}
									/>
								</>
							)}
						</>
					)}
				/>
			)}
		</>
	);
};

export default EditDelivery;
